import { graphHooks } from '@tunasong/graph-lib/react'
import type { UserConfig } from '@tunasong/manifest'
import { useGlobalAppContext } from '@tunasong/plugin-lib'
import type { UserConfigService } from '@tunasong/redux'
import { ConfigService, useSelector, useStore, useThunkDispatch } from '@tunasong/redux'
import { useEffect } from 'react'

/**
 * Async user configuration service. This loads configuration async, so clients that must
 * have updated information should use this and wait for the promise to resolve.
 *
 * Others can use the synchronous version(s) that look like useState() hooks.
 */

export const useAsyncUserConfigService = (): UserConfigService => {
  /** Currently we use the profile to store configuration */
  const { profile } = graphHooks.useProfile()
  const dispatch = useThunkDispatch()
  const { getState, subscribe } = useStore()
  const { configService } = useGlobalAppContext()

  // This is a bit of an initialization hack
  useEffect(() => {
    const service = configService as ConfigService
    if (service.ready || !profile) {
      return
    }
    service.init({ dispatch, getState, subscribe, profile })
  }, [configService, dispatch, getState, profile, subscribe])

  return configService
}

/** Use function overloads to resolve the generics (thanks co-pilot) */
export function useUserConfig<T extends keyof UserConfig>(
  key: T
): [config: UserConfig[T], updateConfig: (value: UserConfig[T]) => void, hasLoaded: boolean]

export function useUserConfig<T extends keyof UserConfig | Partial<UserConfig>>(): [
  config: UserConfig,
  updateConfig: (keyOrValue: T, value?: T extends keyof UserConfig ? UserConfig[T] : never) => void,
  hasLoaded: boolean,
]

export function useUserConfig<T extends keyof UserConfig>(key?: T) {
  const userConfig = useSelector(state => state.config.userConfig)
  const hasLoaded = useSelector(state => state.config.userConfigLoaded)
  /**
   * Read the config service from the app context for updates only. The read
   * path must NOT route through `useAsyncUserConfigService`: it adds an RTK
   * Query profile subscription and an init effect per call, and this hook is
   * called by leaf components — e.g. every chord chip, twice, via the theory
   * hooks (GH #1548). Initialization is owned by a single always-mounted
   * caller in the app container (`UserConfigBootstrap`).
   */
  const { configService } = useGlobalAppContext()

  if (key) {
    const value = userConfig[key]
    // Explicitly assert the return type for the specific key case
    const updateConfig = (value: UserConfig[T]) => configService.updateUserConfigByKey(key, value)
    return [value, updateConfig, hasLoaded]
  }
  // For the general case, we might need to adjust based on your UserConfig type structure
  // Assuming updateUserConfig can be used directly if no key is provided
  // Explicitly assert the return type for the general case
  return [userConfig, configService.updateUserConfig.bind(configService), hasLoaded]
}

/**
 * Like `useUserConfig(key)`, but the updater returns a promise that resolves only when the profile write succeeds and
 * rejects when it fails (or when the value does not parse). For UIs that must say "saved" only once it is true.
 */
export const useUserConfigSave = <T extends keyof UserConfig>(
  key: T
): [config: UserConfig[T], save: (value: UserConfig[T]) => Promise<void>, hasLoaded: boolean] => {
  const value = useSelector(state => state.config.userConfig[key])
  const hasLoaded = useSelector(state => state.config.userConfigLoaded)
  const { configService } = useGlobalAppContext()
  const save = async (next: UserConfig[T]) => {
    await configService.updateUserConfigByKey(key, next)
  }
  return [value, save, hasLoaded]
}
